import { useEffect, useState, type ReactNode } from "react";
import { SpnLogo } from "@/components/brand/spn-logo";

// Polls /api/healthz and, when MAINTENANCE_MODE is on, replaces the entire
// app with a friendly maintenance message. The API returns 503 for every
// non-health route in that mode so React Query calls will also fail
// noisily — the gate keeps the UI honest about what's happening.
export function MaintenanceGate({ children }: { children: ReactNode }) {
  const [maintenance, setMaintenance] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      try {
        const base = import.meta.env.BASE_URL;
        const res = await fetch(`${base}api/healthz`, { credentials: "include" });
        const json = (await res.json()) as { maintenance?: boolean };
        if (!cancelled) setMaintenance(Boolean(json.maintenance));
      } catch {
        // network errors don't count as maintenance — show app and let
        // individual queries surface their own errors.
      }
    }
    check();
    const id = window.setInterval(check, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  if (maintenance) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="w-full max-w-md space-y-4 text-center" data-testid="maintenance-page">
          <SpnLogo className="mx-auto h-10 w-10" />
          <h1 className="text-2xl font-semibold">Under maintenance</h1>
          <p className="text-sm text-muted-foreground">
            SPN is briefly offline for scheduled maintenance. Please check back in a few minutes.
          </p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
